// Customer dashboard at /me
// Phases:
//   1. unauthenticated → show phone-OTP login + Care+ teaser
//   2. authenticated (no cars) → onboarding: add 1-2 cars
//   3. authenticated (cars, no package) → big buy-Care+ CTA + Razorpay
//   4. authenticated (active package) → wallet view: active coupons + history
import { useEffect, useState, useCallback } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Loader2, LogOut, Plus, Sparkles, Check, Clock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  customerApi, setCustomerToken, getCustomerToken, loadRazorpayScript,
  isValidPhone, errText, fmtDate, CAR_BRANDS,
} from "@/lib/api";
import {
  trackOtpVerified, trackCompleteRegistration, trackViewContent,
  trackInitiateCheckout, trackPurchase,
} from "@/lib/pixel";

const benefitLabel = (c) => {
  switch (c.benefit) {
    case "FREE": return "FREE";
    case "FLAT_PRICE": return `₹${c.value} flat`;
    case "PERCENT_OFF": return `${c.value}% off`;
    case "AMOUNT_OFF": return `₹${c.value} off`;
    case "PCT_OFF_LABOR": return `${c.value}% off labour`;
    case "SPIN": return "Coupon";
    default: return "";
  }
};

const Header = ({ customer, onLogout }) => (
  <header className="bg-white border-b border-[#E4E4E7] px-5 py-4 sticky top-0 z-30">
    <div className="max-w-5xl mx-auto flex items-center justify-between">
      <a href="/" className="flex items-center gap-3" data-testid="me-home-link">
        <img src="/logo-on-light.png" alt="AutoHQ" className="h-8" />
      </a>
      {customer && (
        <div className="flex items-center gap-3">
          <span className="hidden sm:inline text-sm text-[#52525B]">
            Hi, <span className="font-semibold text-[#0A0A0A]">{customer.name.split(" ")[0]}</span>
          </span>
          <Button
            data-testid="me-logout-button"
            onClick={onLogout}
            variant="outline"
            className="h-9 px-3 text-xs uppercase tracking-wider"
          >
            <LogOut className="w-3.5 h-3.5 mr-1.5" /> Log out
          </Button>
        </div>
      )}
    </div>
  </header>
);

function LoginForm({ onAuthed }) {
  const [stage, setStage] = useState("phone"); // phone | otp
  const [phone, setPhone] = useState("");
  const [name, setName] = useState("");
  const [otp, setOtp] = useState("");
  const [busy, setBusy] = useState(false);

  const sendOtp = async (e) => {
    e?.preventDefault();
    if (!isValidPhone(phone)) return toast.error("Enter a valid 10-digit mobile number");
    setBusy(true);
    try {
      await customerApi.post("/customer/otp/send", { phone });
      setStage("otp");
      toast.success("OTP sent! 📲");
    } catch (err) { toast.error(errText(err)); }
    finally { setBusy(false); }
  };

  const verify = async (e) => {
    e?.preventDefault();
    if (!/^\d{6}$/.test(otp)) return toast.error("Enter the 6-digit OTP");
    setBusy(true);
    try {
      const { data } = await customerApi.post("/customer/auth/verify", { phone, otp, name: name.trim() || undefined });
      setCustomerToken(data.token);
      // Fire OTP verify event with advanced-matching data (phone + name)
      trackOtpVerified({ source: "Care+ Login", phone, name: data.customer?.name });
      if (data.is_new) {
        trackCompleteRegistration({ phone, name: data.customer?.name });
      }
      onAuthed(data.customer, data.is_new);
    } catch (err) {
      const msg = errText(err);
      if (msg.toLowerCase().includes("name")) {
        toast.error("First time? Please enter your name above.");
      } else {
        toast.error(msg);
      }
    }
    finally { setBusy(false); }
  };

  return (
    <div className="max-w-md mx-auto mt-12 sm:mt-20 px-5">
      <motion.div
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white border border-[#E4E4E7] rounded-sm p-7 sm:p-9"
        data-testid="me-login-card"
      >
        <p className="text-xs uppercase tracking-[0.25em] text-[#D4AF37] font-bold mb-3">
          AutoHQ Care+ Members
        </p>
        <h1 className="text-3xl font-semibold tracking-tight text-[#0A0A0A]">
          Welcome back.
        </h1>
        <p className="mt-2 text-[#52525B] text-sm">
          {stage === "phone" ? "Sign in with your mobile number — we'll send an OTP." : "Enter the 6-digit code we just sent."}
        </p>

        {stage === "phone" && (
          <form onSubmit={sendOtp} className="mt-7 space-y-4">
            <div>
              <Label className="text-xs uppercase tracking-wider font-semibold text-[#52525B]">Name <span className="text-[#71717A] normal-case tracking-normal">(first-time only)</span></Label>
              <Input
                data-testid="me-login-name"
                value={name} onChange={(e) => setName(e.target.value)}
                placeholder="e.g. Ravi Kumar"
                className="mt-2 bg-[#FAFAFA] border-[#E4E4E7]"
              />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider font-semibold text-[#52525B]">Mobile Number</Label>
              <Input
                data-testid="me-login-phone"
                value={phone} maxLength={10} inputMode="numeric"
                onChange={(e) => setPhone(e.target.value.replace(/\D/g, ""))}
                placeholder="10-digit mobile" className="mt-2 bg-[#FAFAFA] border-[#E4E4E7]"
              />
            </div>
            <Button data-testid="me-login-send-otp" type="submit" disabled={busy} className="w-full bg-[#0A0A0A] text-white font-bold py-6">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Send OTP"}
            </Button>
          </form>
        )}

        {stage === "otp" && (
          <form onSubmit={verify} className="mt-7 space-y-4">
            <p className="text-xs text-[#71717A]">OTP sent to <span className="font-semibold text-[#0A0A0A]">+91 {phone}</span></p>
            <Input
              data-testid="me-login-otp"
              value={otp} maxLength={6} inputMode="numeric"
              onChange={(e) => setOtp(e.target.value.replace(/\D/g, ""))}
              placeholder="6-digit OTP"
              className="text-center text-2xl tracking-[0.5em] font-bold py-7 bg-[#FAFAFA] border-[#E4E4E7]"
            />
            <Button data-testid="me-login-verify" type="submit" disabled={busy} className="w-full bg-[#D4AF37] hover:bg-[#bf9f33] text-[#0A0A0A] font-bold py-6">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Verify & Continue"}
            </Button>
            <button
              type="button" onClick={() => setStage("phone")}
              className="block w-full text-xs text-[#52525B] hover:text-[#0A0A0A]"
            >
              ← Change number
            </button>
          </form>
        )}
      </motion.div>
    </div>
  );
}

function AddCarCard({ onAdded, max = 2, current = 0 }) {
  const [brand, setBrand] = useState("");
  const [model, setModel] = useState("");
  const [reg, setReg] = useState("");
  const [busy, setBusy] = useState(false);

  if (current >= max) return null;

  const submit = async (e) => {
    e.preventDefault();
    if (!brand) return toast.error("Pick a brand");
    if (!model.trim()) return toast.error("Enter the model");
    setBusy(true);
    try {
      const { data } = await customerApi.post("/customer/cars", { brand, model, registration: reg });
      toast.success("Car added!");
      setBrand(""); setModel(""); setReg("");
      onAdded(data.cars);
    } catch (err) { toast.error(errText(err)); }
    finally { setBusy(false); }
  };

  return (
    <form onSubmit={submit} className="bg-[#FAFAFA] border border-[#E4E4E7] rounded-sm p-5" data-testid="add-car-form">
      <p className="text-xs uppercase tracking-[0.2em] font-bold text-[#D4AF37] mb-3">
        + Add Car {current + 1} of {max}
      </p>
      <div className="grid sm:grid-cols-3 gap-3">
        <Select value={brand} onValueChange={setBrand}>
          <SelectTrigger data-testid="add-car-brand" className="bg-white border-[#E4E4E7]">
            <SelectValue placeholder="Brand" />
          </SelectTrigger>
          <SelectContent>
            {CAR_BRANDS.map((b) => <SelectItem key={b} value={b}>{b}</SelectItem>)}
          </SelectContent>
        </Select>
        <Input
          data-testid="add-car-model"
          value={model} onChange={(e) => setModel(e.target.value)}
          placeholder="Model (e.g. Swift)"
          className="bg-white border-[#E4E4E7]"
        />
        <Input
          data-testid="add-car-reg"
          value={reg} onChange={(e) => setReg(e.target.value.toUpperCase())}
          placeholder="Reg. No. (optional)"
          className="bg-white border-[#E4E4E7]"
        />
      </div>
      <Button data-testid="add-car-submit" type="submit" disabled={busy} className="mt-4 bg-[#0A0A0A] text-white font-bold">
        {busy ? "Adding..." : <><Plus className="w-3.5 h-3.5 mr-1" /> Add Car</>}
      </Button>
    </form>
  );
}

function BuyCarePlus({ customer, onPurchased }) {
  const [busy, setBusy] = useState(false);

  const buy = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const loaded = await loadRazorpayScript();
      if (!loaded) throw new Error("Could not load payment provider. Check your internet.");
      const { data } = await customerApi.post("/customer/care-plus/order", {});
      // Fire InitiateCheckout right before Razorpay modal opens
      trackInitiateCheckout({ order_id: data.order_id });
      const rzp = new window.Razorpay({
        key: data.key_id,
        amount: data.amount,
        currency: data.currency,
        order_id: data.order_id,
        name: "AutoHQ",
        description: data.name,
        image: "/logo-on-light.png",
        prefill: {
          name: data.customer_name,
          contact: data.customer_phone,
        },
        theme: { color: "#D4AF37" },
        modal: {
          ondismiss: () => { setBusy(false); toast("Payment cancelled."); },
        },
        handler: async (resp) => {
          try {
            await customerApi.post("/customer/care-plus/verify", {
              razorpay_order_id: resp.razorpay_order_id,
              razorpay_payment_id: resp.razorpay_payment_id,
              razorpay_signature: resp.razorpay_signature,
            });
            // 💰 Fire Purchase event for ad-platform conversion optimization
            trackPurchase({ value: 1249, order_id: resp.razorpay_order_id });
            toast.success("🎉 Care+ activated! Your coupons are ready.");
            onPurchased();
          } catch (err) {
            toast.error(errText(err));
          } finally {
            setBusy(false);
          }
        },
      });
      rzp.on("payment.failed", (r) => {
        toast.error("Payment failed: " + (r?.error?.description || "unknown"));
        setBusy(false);
      });
      rzp.open();
    } catch (err) {
      toast.error(err.message || errText(err));
      setBusy(false);
    }
  };

  return (
    <div className="mt-6 bg-[#0A0A0A] text-white rounded-sm p-7 sm:p-10" data-testid="buy-care-plus-card">
      <p className="text-xs uppercase tracking-[0.28em] text-[#D4AF37] font-bold">Step 2 — Activate Care+</p>
      <h2 className="mt-3 text-3xl sm:text-4xl font-semibold leading-tight">
        Unlock ₹10,000 of car care
        <br />
        <span className="text-[#D4AF37]">for just ₹1,249/year.</span>
      </h2>
      <p className="mt-3 text-sm text-white/70">
        20+ redemptions across washes, AC, alignment, denting, full services. Valid for 1 year on your registered car{customer?.cars?.length > 1 ? "s" : ""}.
      </p>
      <Button
        data-testid="pay-care-plus-button"
        onClick={buy} disabled={busy}
        className="mt-6 bg-[#D4AF37] hover:bg-[#bf9f33] text-[#0A0A0A] font-bold text-base px-8 py-6 rounded-sm"
      >
        {busy ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Opening payment…</> : "Pay ₹1,249 & Activate"}
      </Button>
      <p className="mt-3 text-[11px] text-white/50">
        Powered by Razorpay · UPI · Cards · NetBanking · 100% secure
      </p>
    </div>
  );
}

function CouponCard({ coupon }) {
  const expired = new Date(coupon.expires_at) < new Date();
  const used = (coupon.uses_left ?? 0) <= 0;
  const dead = expired || used;
  return (
    <div
      data-testid={`coupon-card-${coupon.id}`}
      className={`rounded-sm border p-4 transition-colors ${
        dead
          ? "bg-[#FAFAFA] border-[#E4E4E7] opacity-60"
          : "bg-white border-[#E4E4E7] hover:border-[#D4AF37]"
      }`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <p className={`text-sm font-bold leading-tight ${dead ? "line-through text-[#71717A]" : "text-[#0A0A0A]"}`}>
            {coupon.label}
          </p>
          <div className="mt-1.5 flex items-center gap-2 text-[11px] uppercase tracking-wider text-[#71717A]">
            <span className="bg-[#0A0A0A] text-[#D4AF37] rounded-sm px-1.5 py-0.5 font-bold">
              {benefitLabel(coupon)}
            </span>
            <span>Code: {coupon.code}</span>
          </div>
        </div>
        <div className="text-right shrink-0">
          <p className="text-2xl font-extrabold text-[#0A0A0A] leading-none">
            {coupon.uses_left}<span className="text-xs font-medium text-[#71717A]">/{coupon.uses_total || coupon.uses_left}</span>
          </p>
          <p className="text-[10px] uppercase tracking-wider text-[#71717A] mt-1">uses left</p>
        </div>
      </div>
      <div className="mt-3 flex items-center justify-between text-[11px] text-[#71717A]">
        <span className="flex items-center gap-1">
          <Clock className="w-3 h-3" />
          {expired ? "Expired" : `Valid till ${fmtDate(coupon.expires_at)}`}
        </span>
        {coupon.source === "CARE_PLUS" && (
          <span className="text-[#D4AF37] font-bold uppercase tracking-wider">Care+</span>
        )}
        {coupon.source === "SPIN" && (
          <span className="text-[#FF5A00] font-bold uppercase tracking-wider">Spin&Win</span>
        )}
        {coupon.source === "CHAT" && (
          <span className="text-[#FF5A00] font-bold uppercase tracking-wider">Chat Game</span>
        )}
      </div>
    </div>
  );
}

function Dashboard({ customer, onRefresh }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data: d } = await customerApi.get("/customer/me");
      setData(d);
    } catch (e) {
      // 401 likely — kick out
      setCustomerToken(null);
      onRefresh();
    } finally { setLoading(false); }
  }, [onRefresh]);

  useEffect(() => { load(); }, [load]);

  if (loading || !data) return (
    <div className="max-w-5xl mx-auto px-5 py-12 text-center text-[#71717A]">
      <Loader2 className="w-6 h-6 animate-spin mx-auto" />
    </div>
  );

  const { cars = [], active_coupons = [], history_coupons = [], package: pkg } = data;
  const greeting = `Hi ${data.customer.name.split(" ")[0]} 👋`;

  return (
    <div className="max-w-5xl mx-auto px-5 py-10 space-y-8">
      <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}>
        <p className="text-xs uppercase tracking-[0.22em] text-[#D4AF37] font-bold">{greeting}</p>
        <h1 className="mt-2 text-3xl sm:text-4xl font-semibold tracking-tight text-[#0A0A0A]">
          Your AutoHQ Garage
        </h1>
      </motion.div>

      {/* Cars */}
      <section data-testid="cars-section">
        <h2 className="text-sm uppercase tracking-wider font-bold text-[#52525B] mb-3">Your Cars ({cars.length}/2)</h2>
        {cars.length === 0 ? (
          <p className="text-sm text-[#71717A] mb-4">Add your car to unlock Care+ and start saving.</p>
        ) : (
          <div className="grid sm:grid-cols-2 gap-3 mb-4">
            {cars.map((c) => (
              <div key={c.id} className="bg-white border border-[#E4E4E7] rounded-sm p-4" data-testid={`car-${c.id}`}>
                <p className="font-bold text-[#0A0A0A]">{c.brand} {c.model}</p>
                {c.registration && <p className="text-xs text-[#71717A] mt-1 font-mono">{c.registration}</p>}
              </div>
            ))}
          </div>
        )}
        <AddCarCard current={cars.length} onAdded={() => load()} />
      </section>

      {/* Package state */}
      {!pkg && cars.length > 0 && <BuyCarePlus customer={data.customer} onPurchased={load} />}

      {pkg && (
        <section data-testid="active-package">
          <div className="bg-[#0A0A0A] text-white rounded-sm p-6 sm:p-7 flex items-center justify-between flex-wrap gap-3">
            <div>
              <p className="text-xs uppercase tracking-[0.22em] text-[#D4AF37] font-bold flex items-center gap-2">
                <Sparkles className="w-3.5 h-3.5" /> Care+ Active
              </p>
              <p className="mt-1.5 text-lg font-bold">Valid till {fmtDate(pkg.expires_at)}</p>
            </div>
            <div className="text-right">
              <p className="text-3xl font-extrabold">{active_coupons.length}</p>
              <p className="text-[11px] uppercase tracking-wider text-white/60">coupons available</p>
            </div>
          </div>
        </section>
      )}

      {/* Active coupons */}
      <section data-testid="active-coupons-section">
        <h2 className="text-sm uppercase tracking-wider font-bold text-[#52525B] mb-3">
          Active Coupons ({active_coupons.length})
        </h2>
        {active_coupons.length === 0 ? (
          <p className="text-sm text-[#71717A]">No active coupons yet. Buy Care+ or play Spin & Win.</p>
        ) : (
          <div className="grid sm:grid-cols-2 gap-3">
            {active_coupons.map((c) => <CouponCard key={c.id} coupon={c} />)}
          </div>
        )}
      </section>

      {history_coupons.length > 0 && (
        <section data-testid="history-coupons-section">
          <h2 className="text-sm uppercase tracking-wider font-bold text-[#52525B] mb-3">
            Used / Expired ({history_coupons.length})
          </h2>
          <div className="grid sm:grid-cols-2 gap-3">
            {history_coupons.map((c) => <CouponCard key={c.id} coupon={c} />)}
          </div>
        </section>
      )}
    </div>
  );
}

export default function Customer() {
  const [customer, setCustomer] = useState(null);
  const [checking, setChecking] = useState(true);
  const [version, setVersion] = useState(0);

  const refresh = useCallback(() => setVersion((v) => v + 1), []);

  // Fire ViewContent on /me page load — anyone landing here is high-intent for Care+
  useEffect(() => { trackViewContent(); }, []);

  useEffect(() => {
    const token = getCustomerToken();
    if (!token) { setChecking(false); return; }
    customerApi.get("/customer/me")
      .then(({ data }) => setCustomer(data.customer))
      .catch(() => { setCustomerToken(null); setCustomer(null); })
      .finally(() => setChecking(false));
  }, [version]);

  const logout = () => {
    setCustomerToken(null);
    setCustomer(null);
    toast.success("Logged out");
  };

  if (checking) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-[#71717A]" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FAFAFA] text-[#0A0A0A]">
      <Header customer={customer} onLogout={logout} />
      {!customer ? (
        <LoginForm onAuthed={(c) => { setCustomer(c); }} />
      ) : (
        <Dashboard customer={customer} onRefresh={refresh} />
      )}
    </div>
  );
}
